import * as THREE from 'three';

export interface ViewCubeControls {
  readonly target: THREE.Vector3;
  update: () => void;
}

export interface CameraRig {
  camera: THREE.Camera;
  controls: ViewCubeControls;
  keepWorldUp: boolean;
  onChange: () => void;
}

interface ArcballInternals {
  _gizmos?: THREE.Object3D;
  updateMatrixState?: () => void;
}

export const POLE_MARGIN = 0.1;

const WORLD_UP = new THREE.Vector3(0, 1, 0);
const NORTH = new THREE.Vector3(0, 0, 1);
const EPSILON = 1e-6;
const LEVEL_LIMIT = 0.99;
const MIN_SCREEN_TANGENT = 0.3;
const MIN_DURATION_MS = 150;
const MAX_DURATION_MS = 450;

const arcballInternals = (controls: ViewCubeControls) => controls as unknown as ArcballInternals;

const localAxis = (quaternion: THREE.Quaternion, x: number, y: number, z: number) => new THREE.Vector3(x, y, z).applyQuaternion(quaternion);

const horizontal = (vector: THREE.Vector3) => new THREE.Vector3(vector.x, 0, vector.z);

const smoothstep = (t: number) => t * t * (3 - 2 * t);

/* eslint-disable no-underscore-dangle */
// ArcballControls pans by moving its gizmo, not `target`, so the gizmo is the true orbit centre.
export const getPivot = (controls: ViewCubeControls): THREE.Vector3 => (arcballInternals(controls)._gizmos?.position ?? controls.target).clone();
/* eslint-enable no-underscore-dangle */

export const getHeading = (quaternion: THREE.Quaternion): THREE.Vector3 => {
  const forward = localAxis(quaternion, 0, 0, -1);
  const facing = Math.abs(forward.y) < LEVEL_LIMIT
    ? forward
    : localAxis(quaternion, 0, 1, 0).multiplyScalar(forward.y < 0 ? 1 : -1);
  const heading = horizontal(facing);
  return heading.lengthSq() > EPSILON ? heading.normalize() : NORTH.clone();
};

export const snapToCardinal = (vector: THREE.Vector3): THREE.Vector3 => (Math.abs(vector.x) > Math.abs(vector.z)
  ? new THREE.Vector3(Math.sign(vector.x), 0, 0)
  : new THREE.Vector3(0, 0, Math.sign(vector.z) || 1));

export const orientationFrom = (back: THREE.Vector3, up: THREE.Vector3): THREE.Quaternion => {
  const z = back.clone().normalize();
  const x = up.clone().cross(z).normalize();
  const y = z.clone().cross(x);
  return new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
};

const uprightUp = (back: THREE.Vector3) => WORLD_UP.clone().sub(back.clone().multiplyScalar(back.dot(WORLD_UP))).normalize();

export const viewQuaternion = (direction: THREE.Vector3, current: THREE.Quaternion, poleMargin = 0): THREE.Quaternion => {
  const back = direction.clone().normalize();
  if (1 - Math.abs(back.y) > EPSILON) return orientationFrom(back, uprightUp(back));

  const heading = snapToCardinal(getHeading(current));
  const vertical = Math.sign(back.y);
  if (poleMargin <= 0) return orientationFrom(back, heading.clone().multiplyScalar(vertical));

  const tilted = new THREE.Vector3(0, vertical * Math.cos(poleMargin), 0).sub(heading.clone().multiplyScalar(Math.sin(poleMargin)));
  return orientationFrom(tilted, uprightUp(tilted));
};

export const signedAngleAboutVertical = (from: THREE.Vector3, to: THREE.Vector3): number => Math.atan2(
  from.z * to.x - from.x * to.z,
  from.x * to.x + from.z * to.z,
);

export const headingQuaternion = (heading: THREE.Vector3, current: THREE.Quaternion): THREE.Quaternion => new THREE.Quaternion()
  .setFromAxisAngle(WORLD_UP, signedAngleAboutVertical(getHeading(current), heading))
  .multiply(current);

export const ringDragAxis = (grabPoint: THREE.Vector3, cameraQuaternion: THREE.Quaternion): THREE.Vector2 => {
  const tangent = WORLD_UP.clone().cross(horizontal(grabPoint)).normalize();
  const screen = new THREE.Vector2(
    tangent.dot(localAxis(cameraQuaternion, 1, 0, 0)),
    -tangent.dot(localAxis(cameraQuaternion, 0, 1, 0)),
  );
  return screen.length() < MIN_SCREEN_TANGENT ? new THREE.Vector2(1, 0) : screen.normalize();
};

const placeCamera = (camera: THREE.Camera, pivot: THREE.Vector3, quaternion: THREE.Quaternion, distance: number) => {
  camera.quaternion.copy(quaternion);
  camera.position.copy(localAxis(quaternion, 0, 0, 1).multiplyScalar(distance).add(pivot));
  camera.up.copy(localAxis(quaternion, 0, 1, 0));
  camera.updateMatrixWorld();
};

const settleControls = ({ camera, controls, keepWorldUp }: CameraRig) => {
  if (keepWorldUp) camera.up.copy(WORLD_UP);
  controls.update();
  arcballInternals(controls).updateMatrixState?.();
};

const pivotDistance = (camera: THREE.Camera, pivot: THREE.Vector3) => {
  const distance = camera.position.distanceTo(pivot);
  return distance > EPSILON ? distance : 1;
};

export const screenFlip = (cameraQuaternion: THREE.Quaternion): number => (localAxis(cameraQuaternion, 0, 1, 0).y < 0 ? -1 : 1);

export const orbitScene = (rig: CameraRig, turn: number, tilt: number, poleMargin = 0): void => {
  const pivot = getPivot(rig.controls);
  const turned = new THREE.Quaternion().setFromAxisAngle(WORLD_UP, -turn).multiply(rig.camera.quaternion);
  const polar = localAxis(turned, 0, 0, 1).angleTo(WORLD_UP);
  const clampedTilt = THREE.MathUtils.clamp(polar + tilt, poleMargin, Math.PI - poleMargin) - polar;
  const tiltAxis = getHeading(turned).cross(WORLD_UP).normalize();
  const orientation = new THREE.Quaternion().setFromAxisAngle(tiltAxis, clampedTilt).multiply(turned);
  placeCamera(rig.camera, pivot, orientation, pivotDistance(rig.camera, pivot));
  settleControls(rig);
  rig.onChange();
};

export const turnScene = (rig: CameraRig, angle: number): void => orbitScene(rig, angle, 0);

export const animateCamera = (rig: CameraRig, target: THREE.Quaternion): (() => void) => {
  const pivot = getPivot(rig.controls);
  const distance = pivotDistance(rig.camera, pivot);
  const start = rig.camera.quaternion.clone();
  const duration = THREE.MathUtils.clamp((start.angleTo(target) / Math.PI) * MAX_DURATION_MS, MIN_DURATION_MS, MAX_DURATION_MS);
  const startTime = performance.now();
  let frame = 0;
  let finished = false;

  const finish = () => {
    finished = true;
    settleControls(rig);
    rig.onChange();
  };

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / duration, 1);
    placeCamera(rig.camera, pivot, start.clone().slerp(target, smoothstep(progress)), distance);
    if (progress >= 1) {
      finish();
      return;
    }
    rig.onChange();
    frame = requestAnimationFrame(step);
  };

  frame = requestAnimationFrame(step);

  return () => {
    if (finished) return;
    cancelAnimationFrame(frame);
    finish();
  };
};
